import { notifyExtension } from './chunk-2AVUNXAY.mjs';
import { getClaims, transformUser } from './chunk-XOI4VJKO.mjs';
import { __export, __async } from './chunk-4U7ZT42S.mjs';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jsx } from 'react/jsx-runtime';

// src/providers/auth/index.tsx
var auth_exports = {};
__export(auth_exports, {
  AuthProvider: () => AuthProvider,
  useAuthContext: () => useAuthContext
});
var AuthContext = createContext({
  isSignedIn: false,
  isLoading: true,
  user: null,
  isAdmin: false,
  signIn: () => {
  },
  signOut: () => {
  },
  getAccessToken: () => __async(null, null, function* () {
    return "";
  })
});
var AuthContextProvider = ({ children, extensionId }) => {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    logout,
    user: auth0User,
    getAccessTokenSilently
  } = useAuth0();
  const [isLoading, setIsLoading] = useState(true);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  useEffect(() => {
    const checkAuth = () => __async(null, null, function* () {
      try {
        const token = yield getAccessTokenSilently();
        const claims = getClaims(token);
        if (claims) {
          setIsAdmin(claims["x-hasura-default-role"] === "admin");
          const userId = claims["x-hasura-user-id"];
          setIsSignedIn(true);
          setUser(transformUser(userId, auth0User));
          setIsLoading(false);
          if (extensionId) {
            notifyExtension({
              id: extensionId,
              type: "AUTH_TOKEN",
              data: token
            });
          }
        }
      } catch (error) {
        console.error("Session validation failed:", error);
      } finally {
        setIsLoading(false);
      }
    });
    if (!auth0Loading) {
      if (isAuthenticated) {
        checkAuth();
      } else {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated, auth0Loading, extensionId, getAccessTokenSilently]);
  const handleSignOut = useCallback(() => __async(null, null, function* () {
    yield logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
    if (extensionId) {
      notifyExtension({
        id: extensionId,
        type: "LOGOUT"
      });
    }
  }), [logout, extensionId]);
  const handleGetAccessToken = useCallback(() => __async(null, null, function* () {
    try {
      const token = yield getAccessTokenSilently();
      return token;
    } catch (error) {
      console.error("Failed to get access token:", error);
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        const recoverableErrors = [
          "login_required",
          // Session expired or invalid
          "consent_required",
          // User consent needed
          "interaction_required",
          // General interaction needed
          "mfa_required",
          // MFA challenge required
          "missing refresh token"
          // No refresh token available
        ];
        const isRecoverableError = recoverableErrors.some(
          (errCode) => errorMessage.includes(errCode)
        );
        if (isRecoverableError) {
          console.warn("Token refresh failed, logging out user");
          yield handleSignOut();
        }
      }
      throw new Error("Failed to get access token. Please sign in again.");
    }
  }), [getAccessTokenSilently, handleSignOut]);
  const contextValue = {
    isSignedIn,
    isLoading,
    user,
    isAdmin,
    signIn: loginWithRedirect,
    signOut: handleSignOut,
    getAccessToken: handleGetAccessToken
  };
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value: contextValue, children });
};
var AuthProvider = ({ config, extensionId, children }) => {
  return /* @__PURE__ */ jsx(
    Auth0Provider,
    {
      domain: config.domain,
      clientId: config.clientId,
      authorizationParams: {
        audience: config.audience,
        redirect_uri: config.redirectUri
      },
      cookieDomain: config.cookieDomain,
      cacheLocation: "localstorage",
      children: /* @__PURE__ */ jsx(AuthContextProvider, { extensionId, children })
    }
  );
};
var useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, auth_exports, useAuthContext };
//# sourceMappingURL=chunk-K3UDKV2P.mjs.map
//# sourceMappingURL=chunk-K3UDKV2P.mjs.map